import { BRAND } from "../lib/brand";

/**
 * Agency-face copy.
 *
 * Layout follows build/Geekcow-Website.svg; wording follows the positioning in
 * build/Business_Plan_Geekcow_2026_v2.md.pdf and Brief_Marketing_Geekcow_2026.md.pdf:
 * the product is the audit layer (security + AVG/GDPR + WCAG), not "we make websites".
 *
 * Deliberately NOT on this page: prices. The price matrix exists in the business
 * plan but publishing it is a commercial decision, not a design one.
 */

/**
 * Hero headline — "We ___ YOUR ___". Both halves rotate, on deliberately
 * different intervals (see AgencyHero) so the pairings keep changing rather
 * than flipping in lockstep.
 */
export const HERO_VERBS = ["CREATE", "BUILD", "THINK", "IMPROVE", "SUPPORT", "SECURE", "DESIGN"] as const;
export const HERO_NOUNS = ["IDEA", "PROJECT", "WEBSITE", "APP"] as const;

export const HERO = {
  // The eyebrow lives in AgencyHero.tsx: "GC" carries its own bold styling.
  subline: "International Web & App Development Agency",
} as const;

/**
 * "Who we are" — transcribed verbatim from build/WHOWEARESection.svg, including
 * the per-phrase colours, which are the exact fills used in that file.
 * Do not paraphrase this: it is the design's copy, not a summary of it.
 */
export const ABOUT = {
  eyebrow: "WHO WE ARE",
  statement: [
    { text: "We are a ", color: BRAND.ink },
    { text: "multidisciplinary team", color: BRAND.red },
    { text: ", that ", color: BRAND.ink },
    { text: "adapts", color: BRAND.teal },
    { text: " to the ", color: BRAND.ink },
    { text: "needs", color: BRAND.blue },
    { text: " and ", color: BRAND.ink },
    { text: "technologies", color: BRAND.navy },
    { text: " required by each project ", color: BRAND.ink },
    { text: "(it's more fun)", color: BRAND.muted },
    { text: ".", color: BRAND.ink },
  ],
  traits: ["Curious", "Creative", "Independent"],
} as const;

export type Service = {
  label: string;
  /** One sentence shown in the card that opens when the pill is clicked.
   *  Deliberately no prices — see the note above SERVICE_PRICING. */
  desc: string;
  /** Rotation in degrees — the pills sit at loose angles in the design. */
  tilt: number;
  /** Vertical nudge in px, so pills in a row never share a baseline. */
  offset: number;
  /** Horizontal nudge in px, to push each row off centre. */
  nudge: number;
  color: string;
};

/**
 * The service pills, laid out as in build/OurServicesSection.svg.
 *
 * This is the union of that file's list (Apps, Websites, E-commerce, Digital
 * Consulting, WordPress Templates, UX/UI, Figma Design, Branding, AI Agents,
 * Digital Transformation) and the compliance work the business plan sells
 * (hardening, AVG, WCAG, audits, maintenance, EU hosting, AI-first MVPs).
 *
 * The SVG lists "Digital Transformation" twice — not reproduced. Its standalone
 * "Design" pill is dropped too, since UX/UI and Figma Design already cover it.
 *
 * Measuring the rects in that file, the pills fall into THREE bands — a left
 * cluster around 24–36%, a middle pair near 47–50%, and a right cluster at
 * 62–72% — not two columns. The order below therefore alternates short and long
 * labels so they wrap into rows of two to four, and `tilt` / `offset` / `nudge`
 * keep any row from settling into a straight line.
 *
 * Two constraints keep the pills from colliding, since `rotate` is a transform
 * and so contributes nothing to layout:
 *  - `nudge` is never negative. A negative margin larger than the flex gap pulls
 *    a pill straight into its neighbour.
 *  - `tilt` stays within ±4°. A 350px pill rotated 6° stands ~18px taller than
 *    its layout box at each end, which is enough to reach the row below.
 * The design's own rotations go to -6.5°, but its pills are further apart.
 */
export const SERVICES: Service[] = [
  { label: "Apps", desc: "Cross-platform web and mobile apps, built and audited to the same security and accessibility bar as everything we ship.", tilt: -3, offset: 10, nudge: 0, color: BRAND.indigo },
  { label: "Digital Consulting", desc: "A senior read on your stack, roadmap and risks — what to build, what to fix, and what to leave alone.", tilt: 4, offset: 34, nudge: 30, color: BRAND.indigo },
  { label: "Websites", desc: "Fast, accessible websites in WordPress or React, handed over with a signed compliance report.", tilt: -2, offset: 4, nudge: 14, color: BRAND.indigo },
  { label: "E-commerce Websites", desc: "Online stores that are secure by default and aligned with EU consumer and privacy rules from day one.", tilt: 2, offset: 28, nudge: 0, color: BRAND.indigo },
  { label: "UX/UI", desc: "Research-led interface design people can actually use — tested against WCAG, not just eyeballed.", tilt: 0, offset: 6, nudge: 36, color: BRAND.indigo },
  { label: "WordPress Templates", desc: "Reusable, hardened WordPress themes that give you a professional site without a full custom build.", tilt: -4, offset: 32, nudge: 8, color: BRAND.indigo },
  { label: "AI Agents", desc: "Custom AI assistants and automations wired into your tools, with the data boundaries thought through first.", tilt: -4, offset: 12, nudge: 44, color: BRAND.navy },
  { label: "Security Hardening", desc: "We close the common holes — plugins, headers, access, backups — and document exactly what changed.", tilt: 3, offset: 30, nudge: 0, color: BRAND.navy },
  { label: "Branding", desc: "Naming, logo and visual identity that hold up across a website, an app and everything in between.", tilt: 0, offset: 4, nudge: 22, color: BRAND.indigo },
  { label: "GDPR / AVG Compliance", desc: "Consent, data flows and privacy notices brought in line with the GDPR/AVG, with the evidence to show it.", tilt: -3, offset: 28, nudge: 6, color: BRAND.indigo },
  { label: "Figma Design", desc: "Design systems and prototypes in Figma that translate cleanly into the final build.", tilt: -4, offset: 8, nudge: 40, color: BRAND.indigo },
  { label: "WCAG Accessibility", desc: "Manual keyboard and screen-reader testing to WCAG 2.2, not just an automated score.", tilt: 2, offset: 34, nudge: 0, color: BRAND.indigo },
  { label: "AI-First MVPs", desc: "A working, AI-powered product in weeks — scoped tight, built to validate the idea, ready to grow.", tilt: -3, offset: 6, nudge: 34, color: BRAND.navy },
  { label: "Compliance Audits", desc: "One report covering security, GDPR/AVG and WCAG, with a prioritised list of what to fix.", tilt: 4, offset: 30, nudge: 10, color: BRAND.indigo },
  { label: "European Hosting", desc: "Managed hosting on EU infrastructure, with backups, monitoring and updates handled for you.", tilt: -2, offset: 10, nudge: 26, color: BRAND.indigo },
  { label: "Digital Transformation", desc: "Moving the manual, paper or legacy parts of your business onto tools that fit how you work.", tilt: -4, offset: 32, nudge: 0, color: BRAND.indigo },
  { label: "Managed Maintenance", desc: "Monthly updates, security patches and compliance checks, so the site stays healthy after launch.", tilt: 3, offset: 8, nudge: 38, color: BRAND.indigo },
];

/**
 * One line about how the studio actually works: a core plus long-standing
 * partners (design, photography, senior development, social media) who join per
 * project. Individual partners are deliberately not named or listed.
 */
export const SERVICES_NOTE =
  "One team of specialists, not one generalist stretched thin. Design, development, photography and social are each handled by someone who does that work every day — people who have shipped projects together before — with a single point of contact for you throughout.";

/**
 * PRICING — not shown on the site.
 *
 * Kept here because it took a decision to assemble and will be wanted again for
 * a proposal template or a dedicated pricing page. [PLAN] entries are verbatim
 * from the service matrix in Business_Plan_Geekcow_2026_v2 §3; [DERIVED] ones
 * were never priced in either document and are proposals to confirm.
 *
 * Deliberately absent: the €600 founder launch offer. Per the v2 plan that is a
 * campaign price for the warm list, and anchoring it publicly would make €600
 * read as the real price.
 */
export const SERVICE_PRICING: { label: string; price: string; note: string; source: "PLAN" | "DERIVED" }[] = [
  { label: "WordPress Websites", price: "€1,200–2,500", note: "one-off", source: "PLAN" },
  { label: "WordPress Templates", price: "€600–1,200", note: "one-off", source: "PLAN" },
  { label: "Compliance Audits", price: "€800–1,500", note: "security + AVG + WCAG", source: "PLAN" },
  { label: "Security Hardening", price: "€800–1,500", note: "standalone or in a build", source: "PLAN" },
  { label: "GDPR / AVG Compliance", price: "€800–1,500", note: "standalone or in a build", source: "PLAN" },
  { label: "WCAG Accessibility", price: "€800–1,500", note: "standalone or in a build", source: "PLAN" },
  { label: "Managed Maintenance", price: "€49–99", note: "per month", source: "PLAN" },
  { label: "European Hosting", price: "Included", note: "with maintenance", source: "PLAN" },
  { label: "AI-First MVPs", price: "€3,000–5,000", note: "per project", source: "PLAN" },
  { label: "UX/UI Design", price: "€1,200–3,000", note: "per project", source: "DERIVED" },
  { label: "Branding", price: "€1,200–3,000", note: "per project", source: "DERIVED" },
  { label: "Digital Consulting", price: "€600–1,500", note: "per engagement", source: "DERIVED" },
];

/**
 * The stack, grouped rather than scrolled.
 *
 * It used to be two auto-scrolling marquees, which put three moving horizontal
 * strips in a row (stack, testimonials, project deck). Grouping makes it static
 * AND more useful: a visitor can see what each tool is actually for, instead of
 * watching an undifferentiated list slide past.
 */
export type StackGroup = { title: string; note: string; items: string[] };

export const STACK_GROUPS: StackGroup[] = [
  {
    title: "Build",
    note: "What the site or app is made of.",
    items: ["WordPress", "PHP", "React", "TypeScript", "Next.js", "Node.js", "Tailwind", "Python", "SQL", "MongoDB"],
  },
  {
    title: "Security & compliance",
    note: "The layer that makes it a Geekcow build.",
    items: ["WCAG 2.2", "GDPR / AVG", "WPScan", "axe DevTools", "Lighthouse", "Cloudflare", "Raidboxes EU"],
  },
  {
    title: "Design & delivery",
    note: "How it gets designed, shipped and kept live.",
    items: ["Figma", "Adobe CC", "CI/CD", "Managed hosting"],
  },
];

export type ProcessStep = {
  n: string;
  title: string;
  body: string;
  /** Small chip on steps that are not part of every engagement. */
  tag?: string;
};

/**
 * One short line per step — these sit under a big numeral, so they stay terse.
 * Steps 06 and 07 are flagged rather than described as automatic: not every
 * client hands over launch, and the monthly work only exists on the full
 * service. Saying so here is more honest than implying all seven always run.
 */
export const PROCESS: ProcessStep[] = [
  {
    n: "01",
    title: "Analyze",
    body: "First we get to know each other, and look at what you have today — what you want, and what you actually need.",
  },
  {
    n: "02",
    title: "Understand",
    body: "Your goals set against your market and audience, plus the rules that genuinely apply to you.",
  },
  {
    n: "03",
    title: "Propose",
    body: "A fixed scope, with the signed compliance report as the deliverable.",
  },
  {
    n: "04",
    title: "Design & Develop",
    body: "Where the magic happens: our experience and tools, AI included for speed, and a build audited by hand.",
  },
  {
    n: "05",
    title: "Testing",
    body: "Automated scans, plus manual keyboard and screen-reader testing.",
  },
  {
    n: "06",
    title: "Launch",
    tag: "Optional",
    body: "We deploy to managed European hosting with backups and monitoring — or hand it all over to your team.",
  },
  {
    n: "07",
    title: "Iterate",
    tag: "Full service",
    body: "Updates, security patches and compliance checks, every month, for as long as we look after it.",
  },
];

/**
 * Closing statement for the process section — transcribed from
 * build/OURPROCESSSection.svg, with that file's per-phrase fills.
 */
export const PROCESS_STATEMENT: { text: string; color: string }[] = [
  { text: "We assess your ", color: BRAND.ink },
  { text: "immediate", color: BRAND.red },
  { text: " and ", color: BRAND.ink },
  { text: "long-term needs", color: BRAND.red },
  { text: " to offer you the best ", color: BRAND.ink },
  { text: "tech solutions", color: BRAND.teal },
  { text: ", so ", color: BRAND.ink },
  { text: "you can get the most", color: BRAND.blue },
  { text: " out of your investment ", color: BRAND.ink },
  { text: "from the start", color: BRAND.navy },
  { text: ".", color: BRAND.ink },
];

/**
 * The USP, from §4 of the marketing brief. Set in a single colour on purpose:
 * it is a supporting line under the statement above it, not a heading, and
 * multi-colour phrases here competed with the real heading for attention.
 */
export const USP_TEXT =
  "Anyone can generate a website with AI today. The question is whether it is secure, compliant and accessible.";

/**
 * Founder section. Two jobs at once: it makes the studio's credibility concrete
 * (a small studio is bought on who runs it), and it opens the interim door.
 *
 * The CTA is worded for a DIFFERENT buyer on purpose — someone who needs
 * capacity inside their own team, not a website. §5.8 of the business plan
 * names "the freelance eats the agency" as a real risk, so this must not read
 * as a cheaper hourly alternative to a scoped project.
 */
export const FOUNDER = {
  eyebrow: "THE FOUNDER",
  name: "Olga Molina",
  role: "Founder · Product designer & full-stack developer",
  /**
   * Written about the team and how projects get delivered, not about one
   * person's CV — the studio's pitch is that the right specialists show up for
   * each part, and that is what makes a project land.
   */
  body: "Good projects are never solo work. Olga founded Geekcow to build them the way they actually succeed: with the right specialists on each part — designers, photographers, senior developers, social media — people who have worked together before and do that one thing every day. She scopes the project, assembles the team around it and stays your single point of contact from the first call to launch.",
  cta: {
    label: "Hire the founder",
    /** Frames the link for interim/embedded work rather than for project clients. */
    lead: "Need capacity inside your own team instead of a project?",
    note: "Olga takes freelance and interim engagements directly",
  },
} as const;

export const CERTIFICATIONS = [
  {
    name: "Microsoft",
    detail: "Certified (SC-900)",
    href: "https://learn.microsoft.com/en-us/users/olgamolina-5685/credentials/a2188580b7a4d0ee",
  },
  {
    name: "Google",
    detail: "Cybersecurity Professional",
    href: "https://www.coursera.org/account/accomplishments/specialization/Y6Z0QBSU0F3S",
  },
] as const;

/**
 * Primary nav. Ordered to follow the page top-to-bottom so the scroll-spy
 * highlight moves left-to-right as you scroll. Process is included because it
 * carries the studio's differentiator (the audit-and-report USP), and The
 * Founder because a small studio is bought on who runs it. Stack is left out on
 * purpose — it is a supporting detail reachable by scroll and from the footer.
 */
export const NAV_ITEMS = [
  { label: "Home", section: "landing" },
  { label: "About", section: "about" },
  { label: "Process", section: "process" },
  { label: "Services", section: "services" },
  { label: "Projects", section: "projects" },
  { label: "Founder", section: "founder" },
] as const;
